import { userStorageKey } from 'src/domain/authentication/hooks/use-user'
import { ApiResponse } from 'src/models/responses/api-response'
import { webPledgeError, WebPledgeError } from 'src/models/web-pledge-errors'

import { getApiUrl, getCacheBusterValue, getCdnUrl, getEnvironment } from '../environment-utilities'
import { clearFromStorage, getFromStorage, putInStorage } from '../storage-utilities'
import { sessionExpiredEvent, sessionExpiredEventMessage, sessionExpiredEventName, setIsAuthenticated, setSessionExpired } from '../../services/auth-service'
import { HttpCode } from 'src/constants/http-response-code-type'

export interface RequestConfig {
    readonly throwErrorObject?: boolean
    readonly hideLoadingMask?: boolean
    readonly ignoreErrors?: boolean
    readonly overrideNonUserPresentableErrors?: string
    readonly overrideAllErrors?: string
}

interface Configuration {
    readonly ApiUrl: string
    readonly CacheBuster: string
    readonly CdnUrl: string
    readonly Environment: string
}

declare global {
    interface Window {
        WebPledgeApiClient?: any
    }
}

class ApiClient {
    public apiBaseUrl: string | undefined
    public cacheBuster: string | undefined
    public cdnUrl: string | undefined
    public environment: string | undefined

    constructor() {
        this.loadConfiguration()
    }

    private configurationPromise: Promise<Configuration> | undefined

    public readonly loadConfiguration = async () => {
        if (!this.configurationPromise) {
            this.configurationPromise = new Promise(async resolve => {
                const versionNumber = 1
                const environment = getEnvironment()
                const storedResult = getFromStorage<Configuration & { readonly versionNumber: number }>('ui-configuration')
                if (storedResult && versionNumber === storedResult.versionNumber) {
                    resolve(storedResult)
                    return
                }

                const serverResult = {
                    ApiUrl: getApiUrl(environment),
                    CacheBuster: getCacheBusterValue(),
                    CdnUrl: getCdnUrl(environment),
                    Environment: environment,
                }

                putInStorage({ ...serverResult, versionNumber }, 'ui-configuration')
                resolve(serverResult)
                return
            })
        }

        const configuration = await this.configurationPromise
        this.apiBaseUrl = `${configuration.ApiUrl}/api`
        this.cacheBuster = configuration.CacheBuster
        this.cdnUrl = configuration.CdnUrl
        this.environment = configuration.Environment
    }

    public readonly getAssetPath = (path: string) => `${apiClient.cdnUrl}assets/${path}?c=${apiClient.cacheBuster}`

    private readonly tokenLoggedOutMessage: string = 'Token Logged Out'

    public readonly makeRequest = async (url: string, request?: RequestInit | null, config?: RequestConfig | null) => {
        const controller = new AbortController()
        const signal = controller.signal
        window.addEventListener(sessionExpiredEventName, async () => {
            controller.abort(new Error(sessionExpiredEventMessage))
            await this.logout()
            setSessionExpired(true)
        })

        config = config || {}
        request = request || {}
        let response

        try {
            response = await fetch(url, { signal, credentials: 'include', ...request })
        } catch (e: any) {
            if (this.isConnectionError(e)) {
                this.throwError(true, 'Unable to connect to server', config)
            } else {
                this.throwError(false, e.exceptionMessage || e.message, config)
            }
        }

        if (response && !response.ok) {
            if (response.status === HttpCode.UNAUTHORIZED) {
                //-- Since log in has it's own fetch call, we can assume that if any other call fails with an unauthorized response that the user is no longer authenticated
                window.dispatchEvent(sessionExpiredEvent)
            }

            if (response.status === HttpCode.FORBIDDEN) {
                this.logout()
            }

            const error = await this.getError(response)
            this.throwError(error.isMessageUserPresentable, error.message, config)
        }

        return response
    }

    private readonly isConnectionError = (e: Error): boolean => {
        return e && e.message === 'Failed to fetch'
    }

    private readonly throwError = (isMessageUserPresentable: boolean, message: string, config: RequestConfig) => {
        if (config.overrideAllErrors) {
            throw Error(config.overrideAllErrors)
        } else if (config.overrideNonUserPresentableErrors && !isMessageUserPresentable) {
            throw Error(config.overrideNonUserPresentableErrors || 'An error has occurred')
        } else {
            throw Error(message)
        }
    }

    public readonly get = async <T>(url: string, config?: RequestConfig | null) => {
        const response = await this.makeRequest(url, null, config)
        return response ? ((await response.json()) as T) : null
    }

    public readonly post = async <T>(url: string, body?: any, config?: RequestConfig | null) => {
        const request = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: body ? JSON.stringify(body) : null,
        }

        const response = await this.makeRequest(url, request, config)
        return response && response.status !== 204 ? ((await response.json()) as T) : null
    }

    public readonly login = async (username: string, password: string, config?: RequestConfig) => {
        try {
            await this.post<ApiResponse<string>>(`${this.apiBaseUrl}/WebPledge/login`, { username, password }, config)
            setIsAuthenticated(true)
        } catch (e: any) {
            setIsAuthenticated(false)
        } finally {
            clearFromStorage(userStorageKey)
        }
    }

    public readonly logout = async (config?: RequestConfig) => {
        try {
            await this.post<string>(`${this.apiBaseUrl}/logout`, null, config)
        } catch (e: any) {
            console.error(e)
        } finally {
            clearFromStorage(userStorageKey)
            setIsAuthenticated(false)
        }
    }

    public readonly isAppOffline = async (config: RequestConfig) => {
        return await this.get<boolean>('/data-api/MaintenanceMode/GetMaintenanceModeValue', config)
    }

    private readonly getError = async (response: Response): Promise<WebPledgeError> => {
        switch (response.status) {
            case 400:
                return this.get400Error(response)
            default:
                return this.get500Error(response)
        }
    }

    private readonly get400Error = async (response: Response): Promise<WebPledgeError> => {
        return (await response.json()) as WebPledgeError
    }

    private readonly get500Error = async (response: Response): Promise<WebPledgeError> => {
        const json = (await response.json()) as WebPledgeError & { readonly exceptionMessage?: string }

        return webPledgeError(
            json ? json.exceptionMessage || json.message || '' : response.statusText,
            json && json.isMessageUserPresentable,
            json && json.errorCode,
        )
    }
}

if (!window.WebPledgeApiClient) {
    window.WebPledgeApiClient = new ApiClient()
}

export const apiClient: ApiClient = window.WebPledgeApiClient
